import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, Inject, OnInit, TemplateRef } from "@angular/core";
import {
	AbstractControl,
	FormArray,
	FormControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { toPromise } from "@app/_helpers/promise";
import { createRxValue, nextTick } from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { HttpService } from "@app/core";
import { ReportsService } from "@app/pages/reports-page/reports.service";
import { environment } from "@env/environment";
import { UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { fromRxValue } from "../../_helpers/utils";
import { DashboardConfig, DashboardTabTypes } from "./dashboardConfig";

type SavedDashboard = DashboardConfig & {
	editing: boolean;
	editingColor: boolean;
	color: string;
	workspaceId: string;
	user: { id: string; name: string };
	paritionKey: string;
	cosmosEntityName: string;
};
type PinnedDashboard = {
	id: string;
	tabType: DashboardTabTypes;
	workspaceId: string;
};

export const DuplicateName: (dashboards: () => any) => ValidatorFn = (_dashboards) => {
	return (control: AbstractControl): ValidationErrors | null => {
		const dashboards: any = _dashboards();
		if (!dashboards) return null;
		if (!(dashboards.items.length > 0)) return null;

		if (dashboards.items.find((x: any) => x.displayName.toLowerCase() == control.value.toLowerCase())) {
			return { dupelicateName: { value: control.value } };
		}
	};
};

const createFormGroup = (data: SavedDashboard) => {
	return new FormGroup({
		displayName: new FormControl(data.displayName, [
			Validators.required,
			CustomValidators.minLength(3),
			Validators.maxLength(255),
		]),
		id: new FormControl(data.id),
		workspaceId: new FormControl(data.workspaceId),
		user: new FormControl(data.user),
		auditData: new FormControl(data.auditData),
		fromDateData: new FormControl(data.fromDateData),
		toDateData: new FormControl(data.toDateData),
		rangePreset: new FormControl(data.rangePreset),
		columns: new FormControl(data.columns),
		personData: new FormControl(data.personData),
		groupData: new FormControl(data.groupData),
		projectData: new FormControl(data.projectData),
		clientData: new FormControl(data.clientData),
		taskData: new FormControl(data.taskData),
		tagData: new FormControl(data.tagData),
		comegoType: new FormControl(data.comegoType ?? []),
		billable: new FormControl(data.billable),
		editing: new FormControl(false),
		editingColor: new FormControl(false),
		pinned: new FormControl(false),
		tabType: new FormControl(DashboardTabTypes),
		color: new FormControl(data.color),
		contingentUntilDate: new FormControl(data.contingentUntilDate),
	});
};
type UserSettingsWithPinnedDashboards = UserSettings & {
	pinnedDashboards: PinnedDashboard[];
};
@Component({
	selector: "tg-save-dashboard-dialog",
	templateUrl: "./save-dashboard-dialog.component.html",
	styleUrl: "./save-dashboard-dialog.component.scss",
})
export class SaveDashboardDialogComponent implements OnInit {
	readonly nameField = new FormControl("", [CustomValidators.minLength(3), Validators.required]);
	readonly name = new FormControl("", [
		CustomValidators.minLength(3),
		Validators.maxLength(255),
		Validators.required,
		DuplicateName(() => (this.savedDashboardsGroup?.value ? this.savedDashboardsGroup?.value : null)),
	]);
	private colors = [
		"#d43024",
		"#00f43c",
		"#d4cc3f",
		"#35e6dd",
		"#d929e6",
		"#29e668",
		"#732cd1",
		"#d12c81",
		"#d1872c",
		"#404ddb",
	];
	readonly color = new FormControl(this.colors[Math.floor(Math.random() * (this.colors.length - 1)) + 1]);
	readonly createNew = createRxValue<boolean>(false);
	readonly tabType$ = createRxValue<DashboardTabTypes>(this.data.tabType);

	readonly pinnedDashboardsFromWS$ = fromRxValue(
		combineLatest([this.userSettingsQuery.select(), this.tabType$.asObservable()]).pipe(
			map(
				([user, tabType]: [UserSettingsWithPinnedDashboards, DashboardTabTypes]) =>
					user.pinnedDashboards?.filter(
						(x: PinnedDashboard) => x?.workspaceId === user.workspace.id && x.tabType === tabType
					) ?? []
			)
		)
	);
	readonly pinnedDashboardsFromWSRest$ = fromRxValue(
		combineLatest([this.userSettingsQuery.select(), this.tabType$.asObservable()]).pipe(
			map(
				([user, tabType]: [UserSettingsWithPinnedDashboards, DashboardTabTypes]) =>
					user.pinnedDashboards?.filter(
						(x: PinnedDashboard) => x?.workspaceId === user.workspace.id && x.tabType !== tabType
					) ?? []
			)
		)
	);
	readonly pinnedDashboardsDifferentWS$ = fromRxValue(
		combineLatest([this.userSettingsQuery.select()]).pipe(
			map(
				([user]: [UserSettingsWithPinnedDashboards]) =>
					user.pinnedDashboards?.filter((x: PinnedDashboard) => x?.workspaceId !== user.workspace.id) ?? []
			)
		)
	);

	private savedDashboardsGroup = new FormGroup({
		items: new FormArray([]),
	});
	readonly savedDashboards$ = this.savedDashboardsGroup.valueChanges
		.pipe(startWith(this.savedDashboardsGroup.value))
		.pipe(map((x) => x.items));
	get savedDashboardControls() {
		return this.savedDashboardsGroup.get("items") as FormArray;
	}
	private parseDashboard(data: SavedDashboard[]) {
		if (!data) return;
		const user: UserSettingsWithPinnedDashboards =
			this.userSettingsQuery.getValue() as UserSettingsWithPinnedDashboards;

		let parsed = data.map((x) => {
			return {
				id: x.id,
				displayName: x.displayName,
				workspaceId: x.workspaceId,
				user: x.user,
				auditData: x.auditData,
				fromDateData: x.fromDateData,
				toDateData: x.toDateData,
				rangePreset: x.rangePreset,
				personData: x.personData,
				columns: x.columns ?? [],
				groupData: x.groupData,
				projectData: x.projectData,
				clientData: x.clientData,
				taskData: x.taskData,
				tagData: x.tagData,
				comegoType: x.comegoType ?? [],
				contingentUntilDate: x.contingentUntilDate ?? null,
				billable: x.billable,
				editing: false,
				editingColor: false,
				pinned: !!user.pinnedDashboards?.find((pinned: PinnedDashboard) => pinned.id === x.id),
				tabType: x?.tabType ?? 0,
				color: x.color ?? "#000000",
			};
		});

		this.savedDashboardControls.controls = parsed.map((x) => createFormGroup(x as unknown as SavedDashboard));

		this.savedDashboardsGroup.get("items")?.setValue(parsed, { emitEvent: false });

		this.savedDashboardControls.updateValueAndValidity();
		return parsed;
	}
	get userSettings() {
		return this.userSettingsQuery.getValue() as UserSettingsWithPinnedDashboards;
	}
	private loadDashboard() {
		return this.http
			.get<SavedDashboard[]>(
				environment.serverUrl +
					`/get/saved_dashboards?${new URLSearchParams({
						$filter: `editor eq '${this.userSettings.id}' `,
					}).toString()}`
			)
			.toPromise()
			.then((data) => {
				this.parseDashboard(data);
				return data;
			});
	}
	readonly nameField$ = this.nameField.valueChanges.pipe(
		startWith(this.nameField.getRawValue()),
		distinctUntilChanged(),
		map(() => this.nameField.getRawValue())
	);

	readonly pinnedDashboard$ = combineLatest([
		this.nameField$,
		this.savedDashboards$,
		this.pinnedDashboardsFromWS$.asObservable(),
	]).pipe(
		map(([search, data, pinned]) => {
			const TabTypeRoute = this.tabType$.value;
			let controls = this.savedDashboardControls.controls;
			controls = controls.filter((x) => {
				if (x.getRawValue().pinned && x.getRawValue().tabType === TabTypeRoute) return x;
			});

			const orderRef = pinned;

			controls = controls.sort((a, b) => {
				const indexA = orderRef.findIndex((ref) => ref.id === a.getRawValue().id);
				const indexB = orderRef.findIndex((ref) => ref.id === b.getRawValue().id);
				return indexA - indexB;
			});
			return controls.filter(
				(x) => x.getRawValue().displayName.toLowerCase().indexOf(search.toLowerCase()) !== -1
			);
		})
	);
	// readonly pinnedDashboardProject$ = combineLatest([this.nameField$, this.savedDashboards$]).pipe(
	// 	map(([search, data]) => {
	// 		const isProjectTabRoute = this.tabType$.value;
	// 		let controls = this.savedDashboardControls.controls;
	// 		controls = controls.filter((d) => d.getRawValue().pinned == true);
	// 		controls = controls.filter((x) => {
	// 			return x.getRawValue().tabType === isProjectTabRoute;
	// 		});

	// 		const orderRef = this.pinnedDashboardsFromWS$.value;

	// 		controls = controls.sort((a, b) => {
	// 			const indexA = orderRef.findIndex((ref) => ref.id === a.getRawValue().id);
	// 			const indexB = orderRef.findIndex((ref) => ref.id === b.getRawValue().id);
	// 			return indexA - indexB;
	// 		});
	// 		return controls.filter((x) => x.getRawValue().displayName.toLowerCase().indexOf(search.toLowerCase()) !== -1);
	// 	}),
	// );

	readonly filteredControlsNotPinned$ = combineLatest([this.nameField$, this.savedDashboards$]).pipe(
		map(([search, data]) => {
			let controls = this.savedDashboardControls.controls;
			controls = controls.filter((d) => d.getRawValue().pinned == false);

			const isProjectTabRoute = this.data.tabType;
			controls = controls.filter((x) => {
				return x.getRawValue().tabType === isProjectTabRoute;
			});
			return controls.filter(
				(x) => x.getRawValue().displayName.toLowerCase().indexOf(search.toLowerCase()) !== -1
			);
		})
	);

	constructor(
		@Inject(MAT_DIALOG_DATA)
		private data: SavedDashboard,
		private dialog: MatDialog,
		private http: HttpService,
		private userSettingsQuery: UserSettingsQuery,
		private ref: MatDialogRef<SaveDashboardDialogComponent>,
		private appService: AppService,
		private reportsService: ReportsService
	) {}
	ngOnInit(): void {
		this.tabType$.next(this.data.tabType);
		this.ref.updateSize("520px");
		this.loadDashboard();
		this.data.createNew ? this.createNew.next(true) : this.createNew.next(false);
	}

	async submit() {
		const userSettings = this.userSettingsQuery.getValue();
		const user = userSettings.workspace.users.find((u) => {
			return u.id === userSettings.id;
		});
		let newDashboard = {
			displayName: "",
			fromDateData: this.data.fromDateData,
			toDateData: this.data.toDateData,
			rangePreset: this.data.rangePreset,

			personData: this.data.personData,
			columns: this.data.columns,
			groupData: this.data.groupData,
			projectData: this.data.projectData,
			clientData: this.data.clientData,
			taskData: this.data.taskData,
			comegoType: this.data.comegoType,
			tagData: this.data.tagData.map((tag) => tag.id),
			billable: this.data.billable,
			auditData: this.data.auditData,
			user: { id: user.id, name: user.name },
			workspaceId: userSettings.workspace.id,
			tabType: this.data.tabType,
			color: this.color.value,
		};
		this.createNew.next(false);
		newDashboard = { ...newDashboard, displayName: this.name.value };
		const newDashboards = await toPromise(
			this.http.post<any>(environment.serverUrl + "/add/saved_dashboards", newDashboard)
		).then((x) => [x].flat());
		this.addAndRemovePinned(newDashboards[0].id);
		this.parseDashboard([...(this.savedDashboardsGroup.getRawValue().items ?? []), ...newDashboards]);
		this.appService?.notifySuccess("dashboard.view_created");
		this.ref.close(newDashboards[0]);
	}
	async deleteDashboard(control: FormGroup, confirmDeleteTemplate?: TemplateRef<any>) {
		const refItem = control.getRawValue();
		if (!refItem) return;
		if (confirmDeleteTemplate) {
			const deletedDashboard = await toPromise(
				this.dialog
					.open(confirmDeleteTemplate, {
						data: {
							entity: refItem,
						},
					})
					.afterClosed()
			);
			if (deletedDashboard) {
				await toPromise(
					this.http.delete<any[]>(environment.serverUrl + "/delete/saved_dashboards/" + refItem.id, {})
				).then((x) => [x].flat());
				if (refItem.pinned) this.addAndRemovePinned(refItem.id);
				this.parseDashboard(this.savedDashboardsGroup.getRawValue().items.filter((x) => x.id !== refItem.id));
				this.appService?.notifySuccess("dashboard.view_deleted");
			}
		}
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}
	selectItem(control: FormGroup) {
		return this.ref.close(control.getRawValue());
	}
	enableEdit(control: FormGroup, el?: ElRefDirective) {
		control.patchValue({ editing: true });
		control.get("displayName")?.updateValueAndValidity();
		control.updateValueAndValidity();
		if (el) nextTick(() => el.elementRef.nativeElement.focus());
	}
	enablColoreEdit(control: FormGroup, el?: ElRefDirective) {
		control.patchValue({ editingColor: true });
	}
	async updateName(control: FormGroup) {
		const item = control;
		if (!item) return;

		const dupelicate = this.savedDashboardsGroup.value.items.find((x: any) => {
			if (x.displayName.toLowerCase() == item.value.displayName.toLowerCase() && x.id != item.value.id) return x;
		});
		if (dupelicate) {
			this.appService.notifyError("errors.duplicatenames");
			return;
		}

		const displayName = item.get("displayName");
		if (!displayName.getRawValue()) return;
		const color = item.get("color");
		if (!displayName.dirty && !color.dirty) {
			item.patchValue({ editing: false }, { emitEvent: false });
			item.updateValueAndValidity();
			return;
		}
		const [updatedItem] = await toPromise(
			this.http.post<any>(
				environment.serverUrl + "/update/saved_dashboards/" + item.getRawValue().id,
				item.getRawValue()
			)
		).then((x) => [x].flat());
		this.parseDashboard(
			this.savedDashboardsGroup.getRawValue().items.map((x) => (x.id === updatedItem.id ? updatedItem : x))
		);
		item.patchValue({ editing: false }, { emitEvent: false });
		this.appService?.notifySuccess("dashboard.view_renamed");
		this.reportsService.dashboards.update((oldValues) => {
			let newValues = [...oldValues];
			const index = newValues.findIndex((d) => d.id == updatedItem.id);
			if (index == -1) return newValues;
			newValues[index] = {
				...newValues[index],
				...updatedItem,
			};
			return newValues;
		});
		item.updateValueAndValidity();
	}
	onCloseColorPicker(control: FormGroup, el: ElRefDirective, color: string) {
		if (control.getRawValue().color) {
			control.controls.color.setValue(color);
			control.controls.color.markAsDirty();
		}
		this.enableEdit(control, el);
	}

	async addAndRemovePinned(dashboardId: string) {
		let user: any = this.userSettingsQuery.getValue();
		let pinned: PinnedDashboard[] = this.pinnedDashboardsFromWS$.value;
		const restPinned: PinnedDashboard[] = this.pinnedDashboardsFromWSRest$.value;
		if (pinned.find((x) => x.id == dashboardId)) {
			pinned = pinned.filter((x) => {
				return x.id !== dashboardId;
			});
		} else {
			pinned = [...pinned, { id: dashboardId, tabType: this.tabType$.value, workspaceId: user.workspace.id }];
		}
		this.pinnedDashboardsFromWS$.next(pinned);
		user.pinnedDashboards = [
			...this.pinnedDashboardsFromWS$.value,
			...restPinned,
			...this.pinnedDashboardsDifferentWS$.value,
		];
		await toPromise(this.http.post<UserSettings>(environment.serverUrl + "/update/users/" + user.id, user)).then();
		this.parseDashboard([...(this.savedDashboardsGroup.getRawValue().items ?? [])]);
	}

	async drop(event: CdkDragDrop<FormGroup[]>) {
		// TODO: check if order did not change
		let pinned: PinnedDashboard[] = this.pinnedDashboardsFromWS$.value;
		moveItemInArray(pinned, event.previousIndex, event.currentIndex);
		this.pinnedDashboardsFromWS$.next(pinned);
		let user: any = this.userSettingsQuery.getValue();
		const restPinned: PinnedDashboard[] = this.pinnedDashboardsFromWSRest$.value;
		user.pinnedDashboards = [
			...this.pinnedDashboardsFromWS$.value,
			...restPinned,
			...this.pinnedDashboardsDifferentWS$.value,
		];
		await toPromise(this.http.post<UserSettings>(environment.serverUrl + "/update/users/" + user.id, user)).then();
		this.parseDashboard([...(this.savedDashboardsGroup.getRawValue().items ?? [])]);
	}
}
